<template>
  <transition name="auth-overlay-transition">
    <div v-if="show" class="auth-overlay" :style="{ height: overlayHeight }" @click="onOverlayClicked">
      <div
        ref="popup"
        class="auth-popup"
        :class="{ show: show, wide: currentStep === step.VALIDATE }"
        :style="{ top: position.top, left: position.left }"
        @click.stop
      >
        <div v-if="currentStep === step.VALIDATE" class="block-validate auth-block">
          <div class="title">Log in or register to save your Favorites.</div>
          <BannerError :errors="errors" :fields="['oauth', 'server', 'security']"></BannerError>
          <FlexWrapper>
            <div class="item">
              <h2 class="logintype">With your email:</h2>
              <form class="pixel" @submit.prevent="onValidateSubmitted">
                <div class="form-group no-margin field">
                  <label for="email">Email Address</label>
                  <input
                    id="email"
                    ref="emailInput"
                    v-model="account.email"
                    type="email"
                    name=""
                    placeholder="Enter your email here"
                    autocorrect="off"
                    autocapitalize="none"
                  />
                </div>

                <FormError v-if="errors['email']" :errors="errors" :field="'email'"></FormError>

                <input type="submit" value="Continue" class="full" :class="{ disabled: submitting }" />
              </form>
            </div>
            <div class="divider"></div>
            <div class="item">
              <SocialLogin :on-complete="handleLoginResult" :extra="{ registerSource: 'WISHLIST' }" :track-page="'wishlist'"></SocialLogin>
            </div>
          </FlexWrapper>
        </div>

        <div v-else-if="currentStep === step.COMPLETE" class="block-complete auth-block">
          <div class="title">Log in to save your Favorites.</div>
          <div class="typed-email">
            <span class="email">{{ account.email }}</span>
            <a class="change" @click.prevent="onEmailChangeClicked">Change</a>
          </div>

          <form class="pixel" @submit.prevent="onLoginSubmitted">
            <div class="form-group no-margin field">
              <label for="password">Password</label>
              <label class="forgot-password">
                <a href="/forgot">Forgot Password?</a>
              </label>
              <input id="password" v-model="account.password1" type="password" name="" placeholder="Enter your password here" />
            </div>

            <FormError v-if="errors['server']" :errors="errors" :field="'server'"></FormError>
            <input type="submit" value="Log in" class="full" :class="{ disabled: submitting }" />
          </form>
        </div>

        <div v-else-if="currentStep === step.SIGN_UP" class="block-register auth-block">
          <div class="title">Register to save your Favorites.</div>
          <div class="typed-email">
            <span class="email">{{ account.email }}</span>
            <a class="change" @click.prevent="onEmailChangeClicked">Change</a>
          </div>

          <form class="pixel" @submit.prevent="onRegisterClicked">
            <div class="form-group password-group field">
              <label for="password">Create Password</label>
              <div class="password-input">
                <input
                  id="password"
                  ref="password"
                  v-model="account.password1"
                  :type="showPassword ? 'text' : 'password'"
                  name=""
                  placeholder="Enter your password here"
                  required="required"
                />
                <a class="show" @click.prevent="showPassword = !showPassword">{{ showPassword ? 'HIDE' : 'SHOW' }}</a>
              </div>
            </div>

            <FormError v-if="errors['password1']" :errors="errors" :field="'password1'"></FormError>

            <div class="form-group no-margin field">
              <div class="controls">
                <input id="terms-agreed" v-model="account.termsAgreed" type="checkbox" name="termsAgreed" />
                <label class="description" for="terms-agreed">I agree to the <a href="/tou" target="_blank">Terms &amp; Conditions</a>.</label>
              </div>
            </div>

            <FormError v-if="errors['termsAgreed']" :errors="errors" :field="'termsAgreed'"></FormError>

            <div v-if="!isUS" class="form-group no-margin field">
              <div class="controls">
                <input id="newsletter-opt-in" v-model="account.newsletterOptIn" type="checkbox" name="newsletterOptIn" />
                <label for="newsletter-opt-in" class="description">
                  Yes, I would like to receive emails detailing new products and special offers from Article.
                </label>
              </div>
            </div>

            <FormError v-if="errors['server']" :errors="errors" :field="'server'"></FormError>

            <input type="submit" value="Create Account" class="full" :class="{ disabled: submitting }" />
          </form>
        </div>

        <div v-else-if="currentStep === step.SIGN_UP_VERIFY" class="block-signup-verify auth-block">
          <div class="title">You're in! Keep fav-ing your heart out.</div>
          <p>Don't forget to check your email later. We've sent a verification link to confirm you own this email address.</p>
          <button @click="onOverlayClicked">Close</button>
        </div>

        <div v-else-if="currentStep === step.VERIFY_WITH_PASSWORD" class="block-signup-verify auth-block">
          <div class="title">Verify Your Account</div>
          <p>
            We are asking all customers to verify their accounts. To confirm your account, please check your email and click the verification link
            that our team has sent over.
          </p>
          <button @click="onOverlayClicked">Close</button>
        </div>

        <div v-else-if="currentStep === step.VERIFY_WITHOUT_PASSWORD" class="block-signup-verify auth-block">
          <div class="title">Complete Account Set-Up</div>
          <p>
            We've seen your email before, but it looks like you need to finish setting up your account. Please follow the instructions sent to your
            email address.
          </p>
          <button @click="onOverlayClicked">Close</button>
        </div>
        <div v-else-if="currentStep === step.SOCIAL_ONLY" class="block-signup-verify auth-block">
          <div class="title">Create your password</div>
          <p>An email has been sent to your address with a link to create your password.</p>
          <button @click="onOverlayClicked">Close</button>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
/* eslint-disable no-return-await */
import { ref } from 'vue';
import { http, MediaUtils } from '@/pixel/index';
import LocationService from '@/services/LocationService';
import UserService from '@/services/UserService';
import FormError from '@/components/commons/FormError.vue';
import FlexWrapper from '@/components/commons/FlexWrapper.vue';
import BannerError from '@/components/commons/BannerError.vue';
import SocialLogin from '@/components/auth/SocialLogin.vue';
import useCommon from '@/compositions/useCommon';
import useDiscount from '@/compositions/useDiscount';

const SERVER_ERROR = 'We are unable to log you in at this time. Please try again later';

export default {
  name: 'AuthOverlay',
  components: {
    FormError,
    FlexWrapper,
    BannerError,
    SocialLogin,
  },
  props: {
    show: {
      type: Boolean,
    },
    data: {
      type: Object,
      default: () => ({
        show: false,
        domRef: null,
      }),
    },
    dom: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      account: {
        email: '',
        password1: '',
        termsAgreed: false,
        newsletterOptIn: false,
        urlDiscountCode: '',
      },
      currentStep: '',
      step: {
        PASSWORD_LOGIN: 'PASSWORD_LOGIN',
        SIGN_UP: 'SIGN_UP',
        VALIDATE: 'VALIDATE',
        COMPLETE: 'COMPLETE',
        SIGN_UP_VERIFY: 'SIGN_UP_VERIFY',
        SIGN_UP_SOCIAL: 'SIGN_UP_SOCIAL',
        VERIFY_WITH_PASSWORD: 'VERIFY_WITH_PASSWORD',
        VERIFY_WITHOUT_PASSWORD: 'VERIFY_WITHOUT_PASSWORD',
        SOCIAL_ONLY: 'SOCIAL_ONLY',
      },
      errors: {},
      arrowRightPos: '66px',
      windowWidth: '',
      position: {
        top: '0px',
        left: '0px',
      },
      showPassword: false,
      isUS: LocationService.isUS(),
      overlayHeight: null,
      submitting: false,
    };
  },
  created() {
    this.currentStep = this.step.VALIDATE;
    this.account.newsletterOptIn = this.isUS;
    this.account.urlDiscountCode = this.discountCode;
  },

  setup() {
    const { setUser, refreshCartData, refreshFavorites, setCustomerToken } = useCommon();
    const { discountCode } = useDiscount();
    const popup = ref(null);

    return {
      setUser,
      refreshCartData,
      refreshFavorites,
      setCustomerToken,
      popup,
      discountCode,
    };
  },

  methods: {
    onOverlayClicked() {
      this.$emit('update:show', false);
    },

    async handleLoginResult(result) {
      const { userInfo, customerToken } = result;

      if (result) {
        if (result.success && result.loggedIn) {
          this.$emit('loginSuccess');
          this.onOverlayClicked();
          this.errors = {};
          if (customerToken) {
            this.setCustomerToken(result.customerToken);
          }
        } else if (result.step === this.step.VERIFY_WITH_PASSWORD) {
          this.currentStep = this.step.VERIFY_WITH_PASSWORD;
        } else if (result.errors) {
          this.errors = result.errors;
        } else {
          this.errors = { server: [SERVER_ERROR] };
        }
      }
      
      if (userInfo) {
        this.setUser(userInfo);
        this.refreshCartData();
        this.refreshFavorites();
      }
    },

    async onValidateSubmitted() {
      this.submitting = true;
      try {
        const result = await this.validateEmail();

        if (result && result.success && result.step) {
          if (result.step === this.step.PASSWORD_LOGIN) {
            this.currentStep = this.step.COMPLETE;
          } else if (result.step === this.step.SIGN_UP) {
            this.currentStep = this.step.SIGN_UP;
            this.$nextTick(() => {
              if (this.$refs.password) {
                this.$refs.password.focus();
              }
            });
          } else {
            this.currentStep = result.step;
          }

          this.errors = {};
        } else if (result && !result.success && result.errors) {
          this.errors = result.errors;
        }
      } catch (error) {
        console.error(error);
      }

      this.submitting = false;
    },

    async validateEmail() {
      return UserService.validate({
        email: this.account.email,
      });
    },

    onEmailChangeClicked() {
      this.currentStep = this.step.VALIDATE;
    },

    async onLoginSubmitted() {
      this.submitting = true;
      if (this.account.email && this.account.password1) {
        try {
          const result = await this.accountLogin();

          this.handleLoginResult(result);
        } catch (error) {
          console.error(error);
        }
      }

      this.submitting = false;
    },

    async onRegisterClicked() {
      if(!this.account.termsAgreed){
        this.errors = { termsAgreed: "Please accept the Terms and Conditions" }
        return;
      }

      this.submitting = true;
      try {
        const result = await this.accountRegister();

        const { userInfo, customerToken } = result;
        if (userInfo) {
          this.setUser(userInfo);
        }

        if (customerToken) {
          this.setCustomerToken(customerToken);
        }

        if (result && result.step) {
          if (result.step === 'SIGN_UP_VERIFY') {
            this.currentStep = this.step.SIGN_UP_VERIFY;
          }
          this.errors = {};
        } else if (result && result.errors) {
          this.errors = result.errors;
        }
      } catch (error) {
        console.error(error);
      }

      this.submitting = false;
    },

    async accountLogin() {
      return UserService.login({
        email: this.account.email,
        password: this.account.password1,
      });
    },

    async accountRegister() {
      return UserService.register({
        ...this.account,
        registerSource: 'WISHLIST',
      });
    },

    calcPosition() {
      const popupPosition = {
        top: 0,
        left: 0,
      };

      this.overlayHeight = `${document.body.scrollHeight}px`;

      if (this.data.domRef) {
        const dom = this.data.domRef;
        const { left, width, bottom } = dom.getBoundingClientRect();
        popupPosition.top = Math.floor(window.pageYOffset + bottom + 14);
        if (left < 350) {
          popupPosition.left = Math.floor(left + width / 2 - 31);
        } else {
          popupPosition.left = Math.floor(left + width / 2 - 333);
        }
        if (window.innerWidth - popupPosition.left < 720) {
          popupPosition.left -= 400;
        }
      }

      if (MediaUtils.isMobileAndSmaller) {
        const { domRef } = this.data;
        const { right, width } = domRef.getBoundingClientRect();
        const center = Math.round(this.windowWidth - right + width / 2 - 5);
        this.popup.style.setProperty('--arrow-right', `${center}px`);

        this.position = {
          top: `${popupPosition.top}px`,
          left: '0px',
        };
      } else {
        const { domRef } = this.data;
        // eslint-disable-next-line no-unused-vars
        const { left } = domRef.getBoundingClientRect();
        if (left < 350) {
          this.popup.style.setProperty('--arrow-right', '368px');
        } else {
          this.popup.style.setProperty('--arrow-right', this.arrowRightPos);
        }

        this.position = {
          top: `${popupPosition.top}px`,
          left: `${popupPosition.left}px`,
        };
      }
    },

    onWindowResize() {
      this.windowWidth = window.innerWidth;

      if (!this.popup) {
        return;
      }

      this.calcPosition();
    },

    onWindowScroll() {
      this.calcPosition();
    },
  },
  watch: {
    show: {
      handler(value) {
        if (value) {
          this.$nextTick(() => {
            this.onWindowResize();
            window.addEventListener('resize', this.onWindowResize);
            this.onWindowScroll();
            setTimeout(() => {
              const { emailInput } = this.$refs;
              emailInput && emailInput.focus();
            });
          });
        } else {
          window.removeEventListener('resize', this.onWindowResize);
          document.removeEventListener('scroll', this.onWindowScroll);
          this.overlayHeight = null;
          this.currentStep = this.step.VALIDATE;
          this.account = {
            email: '',
            password1: '',
            termsAgreed: false,
            newsletterOptIn: false,
          };
        }
      },
    },
  },
};
</script>

<style lang="postcss">
@import '@/pixel/style/variables';
@import '@/pixel/style/mixins';

.auth-overlay-transition-enter-active,
.auth-overlay-transition-leave-active {
  transition: opacity 150ms ease-out;
}

.auth-overlay-transition-enter-from,
.auth-overlay-transition-leave-to {
  opacity: 0;
}

.auth-overlay {
  position: absolute;
  top: 0px;
  bottom: 0px;
  right: 0px;
  left: 0px;
  z-index: 999;
  background: var(--overlay-color);

  .auth-popup {
    --arrow-right: 68px;
    position: absolute;
    width: 335px;
    padding: 24px 25px 28px;
    transform: translate3d(0px, 0px, 0px);
    @mixin overlay;

    &.wide {
      width: 100%;
      max-width: 720px;
    }

    .auth-block {
      .title {
        font-size: var(--font-size-16);
        line-height: var(--line-height-16);
        font-family: var(--font-regular);
        margin-bottom: 20px;
      }

      .typed-email {
        display: flex;
        margin-top: 4px;

        .email {
          margin-right: 8px;
        }

        .change {
          text-decoration: underline;
        }
      }

      p {
        line-height: 1.2em;
        font-size: var(--font-size-16);
      }
      a {
        text-decoration: underline;
      }

      form.pixel {
        margin-top: 10px;

        .form-group {
          &.password-group {
            margin-bottom: 12px;
          }

          &.no-margin {
            margin: 0px;
            margin-bottom: 10px;
          }

          .controls {
            display: flex;

            input {
              margin-top: 2px;
              margin-right: 8px;
            }
          }

          input[type='text'],
          input[type='email'],
          input[type='password'] {
            width: 100%;
            height: 40px;
          }

          .password-input {
            width: 100%;
            height: 40px;
            background: white;
            border: 1px solid black;
            display: flex;
            align-items: center;

            input[type='password'],
            input[type='text'] {
              margin: 0px;
              flex: 1 0 80%;
              border: none;
              box-shadow: none;
              outline: none;
            }

            .show {
              flex: 1 0 20%;
              display: flex;
              justify-content: center;
              font-size: var(--font-size-14);
              font-family: var(--font-bold);
              letter-spacing: 1px;
            }
          }

          .description {
            font-size: var(--font-size-14);
            line-height: 1.4em;

            a {
              text-decoration: underline;
            }
          }

          .forgot-password {
            float: right;

            a {
              text-decoration: underline;
              font-size: var(--font-size-14);
            }
          }
        }

        .error {
          margin-top: 0px;
          font-size: var(--font-size-16);
        }
      }

      .error {
        color: var(--coral);
        margin-top: 12px;
      }
    }

    .block-signup-verify {
      .title {
        margin-bottom: 12px;
      }

      p {
        margin: 0px;
        margin-bottom: 12px;
      }

      button {
        width: 100%;
      }
    }

    @media (max-width: calc($mobile-breakpoint - 1px)) {
      box-sizing: border-box;
      width: calc(100% - 40px);
      margin: 0px 20px;
      left: 20px;
      right: 20px;
      &.wide {
        width: calc(100% - 40px);
      }
    }
  }
}
</style>
